import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useLogger } from '~Hooks';
import arrowBackIcon from '~Assets/icons/arrow_back.svg';
import cardIcon from '~Assets/icons/MainMenu/credit-card-filled.svg';
import redeemIcon from '~Assets/icons/redeem.svg';
import GlassPill from '../../../Components/GlassPill';
import useSessionActive from '../../../Hooks/useSessionActive';
import StepFrame from '../StepFrame';
import ChoiceButton from '../ChoiceButton';
import GiftCardModal from './GiftCardModal';

/** Same corner slot as ReceiptStep - the z-index lifts the pill over StepFrame's stacking context. */
const BackSlot = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  z-index: 1;
`;

const Relative = styled.div`
  position: relative;
`;

interface Props {
  /** The answer from the receipt step - passed on to whichever payment starts. */
  wantsReceipt: boolean;
  /** Card picked - ask the terminal. */
  onCard: (wantsReceipt: boolean) => void;
  onBack: () => void;
}

/**
 * "How do you want to pay?" - card (the obvious, highlighted choice) or a
 * Baemingo gift card. Only shown with the gift card license; the gift card
 * opens a code modal and pays from there.
 */
const PaymentMethodStep: FC<Props> = ({ wantsReceipt, onCard, onBack }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-payment-method');
  /** A shopper hammering the card button must not start two payments. */
  const chosenRef = useRef(false);

  const handleCard = useCallback(() => {
    if (chosenRef.current) {
      return;
    }
    chosenRef.current = true;
    logger.info('Card chosen as payment method');
    onCard(wantsReceipt);
  }, [onCard, wantsReceipt]);

  const [giftCardOpen, setGiftCardOpen] = useState(false);
  const handleGiftCard = useCallback(() => {
    if (chosenRef.current) {
      return;
    }
    logger.info('Gift card chosen as payment method - opening the code modal');
    setGiftCardOpen(true);
  }, []);
  const handleGiftCardClose = useCallback(() => setGiftCardOpen(false), []);

  /** The idle timeout (or start over) must not leave the modal up for the next shopper. */
  const sessionActive = useSessionActive();
  useEffect(() => {
    if (!sessionActive) {
      logger.info('Session ended - closing the gift card modal');
      setGiftCardOpen(false);
    }
  }, [sessionActive]);

  return (
    <Relative>
      <BackSlot>
        <GlassPill icon={arrowBackIcon} onClick={onBack} testId="epos-payment-method-back">
          {i18n('Express.Receipt.GoBack')}
        </GlassPill>
      </BackSlot>
      <StepFrame
        testId="epos-payment-method-step"
        title={i18n('Express.PaymentMethod.Title')}
        subtitle={i18n('Express.PaymentMethod.SubTitle')}
        actions={
          <>
            <ChoiceButton
              label={i18n('Express.PaymentMethod.GiftCard')}
              icon={redeemIcon}
              onClick={handleGiftCard}
              testId="epos-payment-method-gift-card"
            />
            <ChoiceButton
              primary
              label={i18n('Express.PaymentMethod.Card')}
              icon={cardIcon}
              onClick={handleCard}
              testId="epos-payment-method-card"
            />
          </>
        }
      />
      <GiftCardModal
        open={giftCardOpen}
        onClose={handleGiftCardClose}
        wantsReceipt={wantsReceipt}
      />
    </Relative>
  );
};

export default memo(PaymentMethodStep);
